// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import axiosInstance from "../api/axiosInstance";

/**
 * ===============================
 * DEV LOGGER (disabled in production)
 * ===============================
 */
const devLog = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    console.log("[AuthContext]", ...args);
  }
};

const devWarn = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    console.warn("[AuthContext]", ...args);
  }
};

const devError = (...args) => {
  if (process.env.NODE_ENV !== "production") {
    console.error("[AuthContext]", ...args);
  }
};


/**
 * ===============================
 * Decode JWT expiry
 * ===============================
 */
const getJwtExpiry = (token) => {
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000;
  } catch (err) {
    devError("Failed to decode JWT", err);
    return null;
  }
};


/**
 * ===============================
 * Normalize backend UserSessionResponseDTO
 * ===============================
 */
const normalizeUserSession = (session) => {

  if (!session) {
    devWarn("normalizeUserSession received null session");
    return null;
  }

  const normalized = {
    id: session.userId ?? null,
    userId: session.userId ?? null,

    email: session.email ?? "",
    name: session.name ?? "",

    picture: session.picture ?? null,

    googleId: session.googleId ?? null,

    referralCode: session.referralCode ?? null,
    referralLink: session.referralLink ?? null,

    timeZone:
      session.timeZone ??
      Intl.DateTimeFormat().resolvedOptions().timeZone,

    status: session.status ?? "ACTIVE",

    userType: session.userType ?? "EMAIL"
  };

  devLog("Normalized user:", normalized);

  return normalized;
};


/**
 * ===============================
 * Create context
 * ===============================
 */
const AuthContext = createContext(null);


/**
 * ===============================
 * Provider
 * ===============================
 */
export const AuthProvider = ({ children }) => {

  const [user, setUser] = useState(null);
  const [isSuspended, setIsSuspended] = useState(false);

  const refreshTimeout = useRef(null);


  /**
   * ===============================
   * Load user from localStorage
   * ===============================
   */
  const loadUser = () => {

    devLog("Loading user from localStorage");

    const storedUser = localStorage.getItem("user");

    if (!storedUser) {
      devLog("No stored user found");
      setUser(null);
      return;
    }

    try {

      const parsedUser = JSON.parse(storedUser);

      devLog("User loaded successfully:", parsedUser);

      setUser(parsedUser);

      scheduleTokenRefresh();

    } catch (err) {

      devError("Invalid stored user", err);

      setUser(null);

      localStorage.removeItem("user");
      localStorage.removeItem("token");
    }
  };


  useEffect(() => {

    loadUser();

    const handleAuthChange = () => {
      devLog("Auth change detected (cross-tab)");
      loadUser();
    };

    window.addEventListener("auth-change", handleAuthChange);

    return () => {
      window.removeEventListener("auth-change", handleAuthChange);
    };

  }, []);


  /**
   * ===============================
   * LOGIN (supports new UserSessionResponseDTO)
   * ===============================
   */
  const login = (sessionResponse, token) => {

    devLog("Login attempt with session:", sessionResponse);

    if (!sessionResponse) {
      devError("Login failed: sessionResponse is null");
      return;
    }

    /**
     * GLOBAL SUSPENSION GUARD
     */
    if (sessionResponse.status === "SUSPENDED") {

      devWarn("Blocked login: user suspended");

      localStorage.removeItem("token");
      localStorage.removeItem("user");

      setUser(null);

      return;
    }

    /**
     * Normalize user
     */
    const normalizedUser = normalizeUserSession(sessionResponse);

    if (!normalizedUser) {

      devError("Normalization failed");

      return;
    }

    /**
     * Persist token
     */
    if (token) {
      localStorage.setItem("token", token);
    } else if (sessionResponse.token) {
      localStorage.setItem("token", sessionResponse.token);
    }

    /**
     * Persist user
     */
    localStorage.setItem("user", JSON.stringify(normalizedUser));

    /**
     * Update state
     */
    setUser(normalizedUser);

    devLog("Login successful. User stored.");

    /**
     * Schedule silent refresh
     */
    scheduleTokenRefresh();

    /**
     * Cross-tab sync
     */
    window.dispatchEvent(new Event("auth-change"));
  };


  /**
   * ===============================
   * LOGOUT
   * ===============================
   */
  const logout = () => {

    devLog("Logout initiated");

    clearTimeout(refreshTimeout.current);

    localStorage.removeItem("token");
    localStorage.removeItem("user");

    setUser(null);

    window.dispatchEvent(new Event("auth-change"));
  };


  /**
   * ===============================
   * Schedule silent refresh
   * ===============================
   */
  const scheduleTokenRefresh = () => {

    const token = localStorage.getItem("token");

    if (!token) {
      devWarn("No token found, skipping refresh schedule");
      return;
    }

    const expiry = getJwtExpiry(token);

    if (!expiry) {
      devWarn("Token expiry not found");
      return;
    }

    const buffer = 10 * 60 * 1000;

    const refreshIn = expiry - Date.now() - buffer;

    devLog("Token refresh scheduled in ms:", refreshIn);

    if (refreshIn <= 0) {

      devWarn("Token near expiry, refreshing now");

      refreshToken();

      return;
    }

    clearTimeout(refreshTimeout.current);

    refreshTimeout.current =
      setTimeout(refreshToken, refreshIn);
  };


  /**
   * ===============================
   * Refresh token
   * ===============================
   */
  const refreshToken = async () => {

    const oldToken = localStorage.getItem("token");

    if (!oldToken) {

      devWarn("No token to refresh");

      logout();

      return;
    }

    try {

      devLog("Refreshing token...");

      const res = await axiosInstance.post(
        "/auth/refresh",
        null,
        {
          headers: {
            Authorization: `Bearer ${oldToken}`
          }
        }
      );

      const newToken = res.data.token;

      localStorage.setItem("token", newToken);

      devLog("Token refreshed successfully");

      scheduleTokenRefresh();

    } catch (err) {

      devError("Token refresh failed", err);

      logout();
    }
  };


  /**
   * ===============================
   * Provider
   * ===============================
   */
  return (
    <AuthContext.Provider
      value={{
        user,
        isSuspended,
        setIsSuspended,
        login,
        logout
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};


/**
 * ===============================
 * Hook
 * ===============================
 */
export const useAuth = () =>
  useContext(AuthContext);